import { OktaAuth } from '@okta/okta-auth-js';

// TID-W (Tyler Identity Gateway) — use OIDC discovery to find correct endpoints.
// Discovery URL: {issuer}/.well-known/openid-configuration
// Explicit URL overrides are set via VITE_OKTA_AUTHORIZE_URL etc. if discovery is unreachable.
// Fallback keeps the constructor from throwing when VITE_OKTA_ISSUER is absent (e.g. DEV_MODE CI),
// where oktaAuth is never actually invoked (useOktaAuth short-circuits to mock data).
const issuer =
  (import.meta.env.VITE_OKTA_ISSUER as string | undefined) ||
  'https://placeholder.okta.example.com/oauth2/default';

const explicitUrls = import.meta.env.VITE_OKTA_AUTHORIZE_URL
  ? {
      authorizeUrl: import.meta.env.VITE_OKTA_AUTHORIZE_URL as string,
      tokenUrl: import.meta.env.VITE_OKTA_TOKEN_URL as string,
      userinfoUrl: import.meta.env.VITE_OKTA_USERINFO_URL as string,
      logoutUrl: import.meta.env.VITE_OKTA_LOGOUT_URL as string,
    }
  : {};

export const oktaAuth = new OktaAuth({
  issuer,
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID,
  redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI ?? `${window.location.origin}/auth/callback`,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  // TID-W sets frame-ancestors: none — disable all iframe-based SDK services
  // (silent renew, session checks) to prevent CSP violations
  services: {
    autoRenew: false,
    autoRemove: false,
    syncStorage: false,
  },
  tokenManager: {
    autoRenew: false,
  },
  ...explicitUrls,
});

export default oktaAuth;

/**
 * TID-W puts group claims in the access token JWT, not the userinfo response.
 * Decode the token payload directly to extract them.
 */
export function extractGroupsFromToken(accessToken: string | undefined): string[] {
  if (!accessToken) return [];
  try {
    const b64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>;
    return Array.isArray(payload['groups']) ? (payload['groups'] as string[]) : [];
    /* c8 ignore next */
  } catch {
    /* c8 ignore next */
    return [];
  }
}
